import { forwardRef, useRef, Ref, ComponentProps } from "react";
import styled, { css } from "styled-components";

const uniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

const useConstant = <T,>(init: () => T): T => {
  const ref = useRef<T | null>(null);

  if (ref.current === null) {
    ref.current = init();
  }

  return ref.current;
};

type SizeProps = { size?: "xs" | "sm" | "md" | "lg" };

const sizes = ({ size }: SizeProps) => {
  switch (size) {
    case "lg":
      return css`
        font-size: 16px;
      `;

    case "md":
      return css`
        font-size: 12px;
      `;

    case "sm":
      return css`
        font-size: 8px;
      `;
    case "xs":
    default:
      return css`
        font-size: 7px;
      `;
  }
};

const BaseSwitch = forwardRef<
  HTMLInputElement,
  ComponentProps<"input"> & { inputRef?: Ref<HTMLInputElement> }
>(
  (
    { id, checked, onChange, onBlur, className, disabled = false },
    ref: Ref<HTMLInputElement>
  ) => {
    const uid = useConstant(uniqueId);

    return (
      <>
        <input
          className={className}
          id={id ?? uid}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          ref={ref}
        />
        <label htmlFor={id ?? uid} tabIndex={0} role="checkbox">
          <span />
        </label>
      </>
    );
  }
);

export const Switch = styled(BaseSwitch)<SizeProps>`
  height: 0;
  width: 0;
  visibility: hidden;

  & ~ label {
    ${sizes};
    position: relative;

    display: flex;
    align-items: center;
    justify-content: space-between;

    width: 6.25em;
    height: 3.125em;

    background-color: #93a0ae;
    border-radius: 100px;

    cursor: pointer;

    transition: background-color 0.2s;
  }

  & ~ label > span {
    content: "";
    position: absolute;

    left: 10%;
    width: 30%;
    top: 20%;
    height: 60%;

    border-radius: 50%;
    background-color: #fff;
    box-shadow: 0 0 2px 0 rgba(10, 10, 10, 0.29);

    transition: all 0.25s ease-out;
  }

  & ~ label:focus,
  & ~ label:focus-visible,
  & ~ label:focus-within {
    outline-width: 1px;
    outline-color: #93a0ae;
    outline-offset: 2px;
    outline-style: dotted;
  }

  &:checked ~ label {
    background-color: #49b724;
  }

  &:checked ~ label span {
    left: 90%;
    transform: translateX(-100%);
  }

  &:disabled ~ label {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:disabled ~ label:active {
    pointer-events: none;
  }

  &:disabled ~ label span {
    width: 30%;
  }
`;
